import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import RecyclingIcon from "@mui/icons-material/Recycling";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import HistoryEduOutlinedIcon from "@mui/icons-material/HistoryEduOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import type { CommercialRegistrationAnalysisSummary } from "../types";
import {
  STORED_PICTURE_IN_PROGRESS_LABEL,
  storedPictureStatusLabels,
} from "../constants";
import { buildAcquireUrl } from "../utils/paths";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";
import { EMPTY_DISPLAY, formatDateTime } from "../utils/format";
import { canDownloadPdf, useCommercialPdfDownload } from "../utils/download";
import { DataTable } from "./DataTable";
import { EmptyAlert } from "./EmptyAlert";
import { Fields } from "./Fields";

// DB BigInt は10進文字列で受け取るため BigInt で桁区切りする（Number では丸めが起きる）。
const integer = (value: string | null, unit: string): string => {
  if (value === null || !/^-?\d+$/.test(value)) return EMPTY_DISPLAY;
  return `${BigInt(value).toLocaleString("ja-JP")}${unit}`;
};

export const CommercialAnalysisTab: React.FC<{
  summaries: CommercialRegistrationAnalysisSummary[];
  corporateNumberLast12: string;
  corporateNumber: string;
  name: string | null;
  hasAcquired: boolean;
  reuseAvailable: boolean;
}> = ({
  summaries,
  corporateNumberLast12,
  corporateNumber,
  name,
  hasAcquired,
  reuseAvailable,
}) => {
  const searchUrl = buildAcquireUrl(corporateNumberLast12, name);
  const downloadPdf = useCommercialPdfDownload(corporateNumber);
  // 閲覧専用ユーザーは取得できないため、取得の導線を押せないようにする
  const isReadOnly = useReadOnlyUser();

  if (!hasAcquired)
    return (
      <Card
        variant="outlined"
        sx={{ borderRadius: 3, borderColor: "primary.light" }}
      >
        <CardContent
          sx={{ p: { xs: 3, sm: 5 }, "&:last-child": { pb: { xs: 3, sm: 5 } } }}
        >
          <Stack spacing={3} alignItems="flex-start">
            <Box
              sx={{
                p: 2,
                bgcolor: "action.hover",
                borderRadius: 2,
                color: "primary.main",
              }}
            >
              <DescriptionOutlinedIcon fontSize="large" />
            </Box>
            <Chip label="未取得" size="small" variant="outlined" />
            <Box>
              <Typography variant="h5" component="h2" gutterBottom>
                商業登記から、企業情報を詳しく
              </Typography>
              <Typography color="text.secondary">
                商業登記は未取得です。会社概要・事業目的・役員などの情報を確認できます。
              </Typography>
            </Box>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              <ReadOnlyTooltip isReadOnly={isReadOnly}>
                <Button
                  component={RouterLink}
                  to={searchUrl}
                  variant="outlined"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  disabled={isReadOnly}
                >
                  商業登記を取得
                </Button>
              </ReadOnlyTooltip>
              {reuseAvailable ? (
                <ReadOnlyTooltip isReadOnly={isReadOnly}>
                  <Button
                    component={RouterLink}
                    to={buildAcquireUrl(corporateNumberLast12, name, {
                      reuse: true,
                      returnCorporateNumber: corporateNumber,
                    })}
                    variant="outlined"
                    size="large"
                    startIcon={<RecyclingIcon />}
                    disabled={isReadOnly}
                  >
                    リユースで取得
                  </Button>
                </ReadOnlyTooltip>
              ) : null}
            </Stack>
            {reuseAvailable ? (
              <Typography variant="body2" color="text.secondary">
                リユースとは、他のユーザーが過去に取得した同一法人の商業登記データを再利用して取得できる仕組みです。新規取得より早く・低コストでご利用いただけます。
              </Typography>
            ) : null}
          </Stack>
        </CardContent>
      </Card>
    );
  // 取得依頼は成功したが解析結果がまだ無い（解析待ち・解析失敗・未依頼）場合。
  if (summaries.length === 0)
    return (
      <Stack spacing={2} alignItems="flex-start">
        <EmptyAlert>
          商業登記は取得済みですが、解析結果がまだありません。解析の完了後に表示されます。
        </EmptyAlert>
        <Button
          component={RouterLink}
          to="/commercialbook/list"
          variant="outlined"
          endIcon={<ArrowForwardIcon />}
        >
          取得済商業登記を確認
        </Button>
      </Stack>
    );
  return (
    <Stack spacing={2}>
      {summaries.map((s, index) => (
        <Paper
          key={s.analyzedCorporationId ?? index}
          variant="outlined"
          sx={{ p: 3 }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            会社概要
          </Typography>
          <Fields
            items={[
              ["法人名", s.tradeName],
              ["会社法人等番号", s.corporateIdentificationNumber],
              ["成立年月日", s.incorporationDate],
              ["本店", s.headOfficeAddress],
              ["公告方法", s.publicNoticeMethod],
              ["資本金の額", integer(s.capitalAmount, "円")],
              ["発行可能株式総数", integer(s.authorizedShares, "株")],
              ["発行済株式の総数", integer(s.issuedShares, "株")],
              ["存続期間", s.existencePeriod],
              ["法人種別", s.corporationType],
            ]}
          />
          <Stack spacing={3} sx={{ mt: 3 }}>
            <Box>
              <Typography variant="h6">事業目的</Typography>
              {s.purposes.length ? (
                <Box component="ul">
                  {s.purposes.map((purpose, i) => (
                    <li key={i}>{purpose.content ?? "—"}</li>
                  ))}
                </Box>
              ) : (
                <EmptyAlert>表示するデータがありません</EmptyAlert>
              )}
            </Box>
            <DataTable
              icon={<PeopleAltOutlinedIcon />}
              title="役員に関する事項"
              headers={["役職", "氏名", "住所", "就任等事由", "変更日"]}
              rows={s.officers.map((officer) => [
                officer.position,
                officer.name,
                officer.address,
                officer.changeReason,
                officer.changeDate,
              ])}
            />
            <DataTable
              icon={<BadgeOutlinedIcon />}
              title="支配人"
              headers={["氏名", "住所", "営業所所在地"]}
              rows={s.managers.map((manager) => [
                manager.name,
                manager.address,
                manager.officeLocation,
              ])}
            />
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>
                機関設置
              </Typography>
              {s.institutions.length ? (
                <Stack direction="row" gap={1} flexWrap="wrap">
                  {s.institutions.map((institution) => (
                    <Chip key={institution} label={institution} />
                  ))}
                </Stack>
              ) : (
                <EmptyAlert>表示するデータがありません</EmptyAlert>
              )}
            </Box>
            {/* 設定が無いこと自体が情報になる（融資先の検討等）ため、0件でも見出しを出す。
                キーが無い（対応前の API）ときは「設定なし」と誤表示せず「未解析」と表示する */}
            <DataTable
              icon={<AccountBalanceOutlinedIcon />}
              title="企業価値担保権"
              headers={[
                "順位",
                "企業価値担保権者",
                "担保権者住所",
                "取扱店",
                "設定日",
                "消滅に関する定め",
              ]}
              rows={(s.enterpriseValueCharges ?? []).map((charge) => [
                charge.rankNumber,
                charge.holderName,
                charge.holderAddress,
                charge.handlingBranch,
                charge.receiptDate,
                charge.extinctionProvision,
              ])}
              emptyMessage={
                s.enterpriseValueCharges == null
                  ? "企業価値担保権は未解析です"
                  : "企業価値担保権の設定はありません"
              }
            />
            {s.registrationRecords.length > 0 && (
              <DataTable
                icon={<HistoryEduOutlinedIcon />}
                title="登記記録"
                headers={["事由", "登記日", "閉鎖日"]}
                rows={[...s.registrationRecords]
                  .sort((a, b) =>
                    (b.registrationDate ?? "").localeCompare(
                      a.registrationDate ?? ""
                    )
                  )
                  .map((record) => [
                    record.content,
                    record.registrationDate,
                    record.closedDate,
                  ])}
              />
            )}
          </Stack>
          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
            取得済み商業登記（原本PDF）
          </Typography>
          {s.storedPicture ? (
            <Stack spacing={2}>
              <Fields
                items={[
                  ["取得依頼日時", formatDateTime(s.storedPicture.requestedAt)],
                  ["取得日", s.storedPicture.commercialPictureReceivedDate],
                  [
                    "取得ステータス",
                    s.storedPicture.status
                      ? storedPictureStatusLabels[s.storedPicture.status]
                      : STORED_PICTURE_IN_PROGRESS_LABEL,
                  ],
                ]}
              />
              <Button
                disabled={!canDownloadPdf(s)}
                onClick={() => {
                  if (!canDownloadPdf(s)) return;
                  void downloadPdf(s);
                }}
                variant="outlined"
                startIcon={<FileDownloadOutlinedIcon />}
                sx={{ alignSelf: "flex-start", whiteSpace: "nowrap" }}
              >
                PDFをダウンロード
              </Button>
            </Stack>
          ) : (
            <EmptyAlert>原本PDFは未取得</EmptyAlert>
          )}
        </Paper>
      ))}
    </Stack>
  );
};
